import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { OtpPopupComponent } from 'src/app/components/otp-popup/otp-popup.component';
import { ApiService } from 'src/app/services/api/api.service';
import { TabControlService } from 'src/app/services/tab-control.service';
import { ValidationService } from 'src/app/services/validators/validation.service';
import { environment } from 'src/environments/environment';
import * as Constants from "../../../constants/constants";
import { checkTime } from 'src/app/util/Helper';
import { NotificationService } from 'src/app/services/notification.service';
import { ConsentItem, ConsentService } from 'src/app/services/consent/consent.service';

@Component({
  selector: 'app-step1-basic',
  templateUrl: './step1-basic.component.html',
  styleUrls: ['./step1-basic.component.scss']
})
export class Step1BasicComponent implements OnInit {
  title: string = 'Milele Trust Application Process';
  subtitle: string = 'Tell us about yourself';

  titleLabel: string = 'Title';
  titleHint: string = 'Title';
  fNameLabel: string = 'First Name';
  fNameHint : string = 'First name';
  mNameLabel : string = 'Middle Name';
  mNameHint : string = 'Middle name';
  surnameLabel : string = 'Surname';
  surnameHint : string = 'Last name';
  phoneLabel : string = 'Primary Phone Number';
  secondaryPhoneLabel : string = 'Secondary Phone Number';
  emailLabel : string = 'Email Address';
  emailHint : string = 'Your email address';
  idLabel: string = 'ID/Passport Number';
  idHint: string = 'Enter ID or Passport Number';

  disclaimer: string = 'Please note that we will be saving your information as you proceed along the steps';

  requiredFieldString: string = 'Required field';

  formErrors: { [key: string]: string } = {
    Title: '',
    firstName: '',
    middleName: '',
    surname: '',
    phone: '',
    secondaryPhone: '',
    email: '',
    idNo: ''
  };
  validationMessages: { [key: string]: {} } = {
    Title: { required: this.requiredFieldString },
    firstName: { required: this.requiredFieldString },
    middleName: { required: this.requiredFieldString },
    surname: { required: this.requiredFieldString },
    phone: { required: this.requiredFieldString, invalid:'Invalid phone number' },
    secondaryPhone: { required: this.requiredFieldString, invalid:'Invalid phone number' },
    email: { required: this.requiredFieldString, email: "Invalid email address" },
    idNo: { required: this.requiredFieldString },
  };

  // Form
  basicForm = this._formBuilder.group({
    Title: [''],
    firstName: ['', Validators.required],
    middleName: [''],
    surname: ['', Validators.required],
    phone: ['', Validators.required],
    secondaryPhone: [''],
    email: ['', [Validators.required, Validators.email]],
    idNo: ['', Validators.required]
  });

  consentProvided: boolean = false;
  otpVerified: boolean = false;
  basicVerified: boolean = false;
  loading: boolean = false;
  loadingText: string = 'Requesting OTP';


  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly notificationService: NotificationService,
    private readonly tabControlService: TabControlService,
    private readonly validationService: ValidationService,
    private readonly apiService: ApiService,
    private readonly consentService: ConsentService
  ) {
  }

  async ngOnInit(): Promise<void> {
    const storageData = await this.tabControlService.getData(this.tabControlService.storageKey);
    if (storageData?.basicData) {
      const basicData = storageData.basicData;

      this.basicForm = this._formBuilder.group({
        Title: [basicData.Title || ''],
        firstName: [`${basicData.FirstName || ''}`, Validators.required],
        middleName: [`${basicData.MiddleName || ''}`],
        surname: [`${basicData.LastName || ''}`, Validators.required],
        phone: [`${basicData.Phone || ''}`, Validators.required],
        secondaryPhone: [`${basicData.SecondaryPhone || ''}`],
        email: [`${basicData.Email || ''}`, [Validators.required, Validators.email]],
        idNo: [`${basicData.IDNo || ''}`, Validators.required]
      });

      if (basicData.Verified) {
        this.basicVerified = true;
      }
    }

    if (this.tabControlService.activeTab == 1) {
      this.populateData();
    } else if (this.tabControlService.activeTab > 1) {
      this.otpVerified = true;
      this.basicVerified = true;
    }
  }

  get region(): any {
    return this.tabControlService.regionType;
  }
  get loadingTitles(): boolean {
    return this.tabControlService.loadingTitles;
  }
  get TitleOptions(): any {
    return this.tabControlService.TitleOptions;
  }

  get basicData(): any {
    return this.tabControlService.basicData;
  }
  
  async populateData(): Promise<void> {
    if (this.TitleOptions.length === 0) {
      this.tabControlService.loadingTitles = true;
      await this.tabControlService.getTitlesList().then(()=> {
        if (this.TitleOptions.length === 0) this.basicForm.get('Title')?.disable();
      });
      this.tabControlService.loadingTitles = false;
    }
  }

  logErrors() {
    this.formErrors = this.validationService.logValidationErrors(
      this.basicForm,
      this.formErrors,
      this.validationMessages
    );
  }

  // Request OTP and open modal
  async openOtpModal() {
    this.loadingText = 'Requesting OTP';
    this.loading = true;
    this.notificationService.isLoading(true);
    try {
      const data = {
        Region: this.region,
        FirstName: this.basicForm.controls['firstName'].value,
        LastName: this.basicForm.controls['surname'].value,
        Phone: this.basicForm.controls['phone'].value,
        Email: this.basicForm.controls['email'].value
      };
  
      this.tabControlService.updateRecord(1,
        {
          ...data,
          SecondaryPhone: this.basicForm.controls['secondaryPhone'].value || '',
          IDNo: this.basicForm.controls['idNo'].value,
          MiddleName: this.basicForm.controls['middleName'].value,
          Title: this.basicForm.controls['Title'].value,
          Verified: false
          
        });
  
      let savedTime = this.tabControlService.OTPRequestStats;
      let response = {Status: 0, Message: 'Initialized'};

      this.tabControlService.OTPValid = checkTime(savedTime.time);

      if (this.tabControlService.OTPValid && savedTime.phone === data.Phone) {
        response.Status = 1;
        response.Message = 'Current OTP is still Valid';

      } else {
        response = await this.apiService.postRequest(
          environment.baseUrl + Constants.otpRequestURL, data);
      }

      if (response.Status === 1) {
        if (this.tabControlService.OTPValid) {
          this.notificationService.viewToast(
            'info',
            `Please enter the OTP that was sent to your ${this.region === 1 ? 'Email' : 'Phone / Email'}` );
        } else {
          const timeStats = {time: Date.now() / 1000, phone: data.Phone};
          this.tabControlService.OTPRequestStats = timeStats;
          this.tabControlService.OTPValid = true;

          if (this.region == 1) { // Diaspora
            this.notificationService.viewToast(
              'success',
              `OTP sent to ${data.Email}.`);
          } else {
            this.notificationService.viewToast(
              'success',
              `OTP sent to ${data.Phone}.`,
              'We have also sent it to the provided Email');
          }
        }

        this.tabControlService.OTPRequestPage = 'Basic';
        this.loadingText = 'Verify Details';

        const dialogRef = this.dialog.open(OtpPopupComponent, {
          disableClose: true,
        });
    
        dialogRef.componentInstance.otpVerified.subscribe((value: boolean) => {
          this.otpVerified = value;
        });
    
        dialogRef.afterClosed().subscribe(() => {
          console.group('OTP Dialog');
          this.basicVerified = this.otpVerified;
          
          if (this.basicVerified) {
            this.tabControlService.updateRecord(1,
              {
                ...data,
                SecondaryPhone: this.basicForm.controls['secondaryPhone'].value || '',
                IDNo: this.basicForm.controls['idNo'].value,
                MiddleName: this.basicForm.controls['middleName'].value,
                Title: this.basicForm.controls['Title'].value,
                Verified: true
              });

              if (this.consentProvided) this.submitForm();
              else this.openConsentForm();
            
          } else {
            this.notificationService.viewToast('warning', 'Please verify your details to proceed');
          }

          console.groupEnd();
        });
      } else {
        this.notificationService.viewToast('error', response.Message);
      }
    } catch (error) {
      console.log(':: Error !! ', error);
      this.notificationService.viewToast('error', 'OTP request failed');
    }
    this.notificationService.isLoading(false);
    this.loading = false;
  }

  confirmValidationStatus(): boolean {
    if (this.basicData?.FirstName && this.basicData.LastName && this.basicData.Phone && this.basicData.Email && this.basicData.IDNo) {    
      if (
        this.basicData.FirstName === this.basicForm.get('firstName')?.value
        && this.basicData.LastName === this.basicForm.get('surname')?.value
        && this.basicData.Phone === this.basicForm.get('phone')?.value
        && this.basicData.Email === this.basicForm.get('email')?.value
        && this.basicData.IDNo.toString() === this.basicForm.get('idNo')?.value?.toString()
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  navigate = async() => {
    this.tabControlService.page1Done = true;
    await this.tabControlService.changeTab(2);
  }
  
  async submitForm(): Promise<void> {
    if (this.basicVerified && this.consentProvided) {
      this.navigate();
    }
  }


  // ==============  CONSENT LOGIC   =================
  openConsentForm = () => {
    const user = this.basicData;

    const consentData = {
      channelId: environment.channelID,
      client: user,
      appId: environment.appID,
      appPass: environment.appPass,
      consentSource: 'Milele Trust Microsite',
      company: 'ILTC',
      persist: false,
      onSubmit: (selectedConsents: ConsentItem[], allConsents: ConsentItem[]) => {
        let allAccepted = true;
        let notAccepted: ConsentItem[] = [];

        allConsents.forEach((consent: ConsentItem) => {
          if (!consent.selected && consent.mandatory) {
            allAccepted = false;
            notAccepted.push(consent);
          }
        })

        if (allAccepted) {
          this.notificationService.viewToast('success', 'Thank you');
          this.consentAndNavigate();

        } else {
          let skippedConsents = notAccepted[0].name;
          if (notAccepted.length > 1) {
            notAccepted.forEach((consent: ConsentItem, index: number) => {
              if (index > 0) {
                if (index == notAccepted.length-1) skippedConsents+= ` & ${consent.name}`;
                else skippedConsents+= `, ${consent.name}`;
              }
            })
          }
          
          // Notify user omitted required consent(s)
          this.notificationService.viewToast(
            'error',
            `You need to agree to the below consent ${(notAccepted.length === 1 ? 'item' : 'items')} in order to proceed`,
            skippedConsents,
            true
          );
        }
      },
      onClose: () => {
        // Handle when user closes without submitting
        this.notificationService.viewToast(
          'error',
          'Action Required',
          'Please accept the terms to continue',
          true
        );
      }
    };

    this.consentService.loadConsentForm(consentData);
  }

  consentAndNavigate = async() => {
    this.consentProvided = true;
    this.consentService.closeConsentForm();
    this.navigate();
  }
  // =================================================

}
